import { useId, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { FleetDiffEntry } from '@shared/api';
import { diffRows } from '../model';
import { PrcMark } from './FleetChip';
import { VisuallyHidden } from './passportParts';
import { fleetLeading, fleetType } from './tokens';

const Wrap = styled.div`
  container: diff / inline-size;
`;

const COLUMNS = 'minmax(0, 1.35fr) minmax(0, 1fr) 16px minmax(0, 1fr)';
const COLUMNS_CREATE = 'minmax(0, 1.35fr) minmax(0, 1fr)';

const Head = styled.div<{ $create: boolean }>`
  display: grid;
  grid-template-columns: ${({ $create }) => ($create ? COLUMNS_CREATE : COLUMNS)};
  gap: ${({ theme }) => theme.spacing(3)};
  padding: ${({ theme }) => `${theme.spacing(1.5)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderStrong};
  font-size: ${fleetType.micro};
  font-weight: 600;
  letter-spacing: 0.04em;
  color: ${({ theme }) => theme.colors.text.secondary};

  @container diff (max-width: 460px) {
    display: none;
  }
`;

const List = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
`;

const Row = styled.li<{ $create: boolean }>`
  display: grid;
  grid-template-columns: ${({ $create }) => ($create ? COLUMNS_CREATE : COLUMNS)};
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: baseline;
  padding: ${({ theme }) => `${theme.spacing(2)} 0`};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-size: ${fleetType.body};
  line-height: ${fleetLeading.normal};

  & > .field {
    color: ${({ theme }) => theme.colors.text.primary};
    text-wrap: pretty;
  }

  & > .field > .block {
    display: block;
    margin-bottom: 2px;
    font-size: ${fleetType.micro};
    font-weight: 600;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & del,
  & ins {
    font-variant-numeric: tabular-nums;
    overflow-wrap: anywhere;
    text-decoration: none;
  }

  & del {
    color: ${({ theme }) => theme.colors.text.secondary};
  }

  & del[data-empty='false'] {
    text-decoration: line-through;
    text-decoration-color: ${({ theme }) => theme.colors.text.muted};
  }

  & ins {
    color: ${({ theme }) => theme.colors.text.primary};
    font-weight: 600;
  }

  & > .arrow {
    color: ${({ theme }) => theme.colors.text.muted};
    text-align: center;
  }

  /* Узко: подпись строкой, ниже «было → стало» обычным текстом — длинное
     значение переносится по словам, а не по буквам в узкой колонке. */
  @container diff (max-width: 460px) {
    display: block;

    & > .field {
      display: block;
      margin-bottom: 2px;
    }

    & > del,
    & > .arrow,
    & > ins {
      display: inline;
      overflow-wrap: break-word;
    }

    & > .arrow {
      margin: ${({ theme }) => `0 ${theme.spacing(1.5)}`};
    }
  }
`;

const More = styled.button`
  margin-top: ${({ theme }) => theme.spacing(2)};
  padding: ${({ theme }) => `${theme.spacing(1)} 0`};
  border: none;
  background: none;
  color: ${({ theme }) => theme.colors.primary};
  font: inherit;
  font-size: ${fleetType.body};
  font-weight: 600;
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
`;

interface DiffListProps {
  diff: Readonly<Record<string, FleetDiffEntry>>;
  /** `create` — «было» всегда пусто: одна колонка значения. */
  mode?: 'change' | 'create';
  /** Сколько полей показать сразу; остальное — по кнопке. */
  limit?: number;
  /** Имя списка для скринридера. */
  label: string;
}

/**
 * «Было → стало» по полям: подписи из схемы паспорта, порядок паспорта,
 * блок — над первым полем блока, поля ПРЦ отмечены.
 */
export function DiffList({ diff, mode = 'change', limit = 12, label }: DiffListProps) {
  const rows = useMemo(() => diffRows(diff), [diff]);
  const [expanded, setExpanded] = useState(false);
  const listId = useId();
  const create = mode === 'create';
  const visible = expanded ? rows : rows.slice(0, limit);
  const hidden = rows.length - visible.length;

  return (
    <Wrap>
      <Head $create={create} aria-hidden="true">
        <span>Поле</span>
        {create ? (
          <span>Значение</span>
        ) : (
          <>
            <span>Было</span>
            <span />
            <span>Стало</span>
          </>
        )}
      </Head>
      <List id={listId} role="list" aria-label={label}>
        {visible.map((r, i) => {
          const firstOfBlock = r.block !== null && r.block !== visible[i - 1]?.block;
          return (
            <Row key={r.key} $create={create}>
              <span className="field">
                {firstOfBlock && <span className="block">{r.block}</span>}
                {r.label}
                {r.prc && (
                  <>
                    {' '}
                    <PrcMark title="Поле участвует в расчёте цены машино-часа">ПРЦ</PrcMark>
                  </>
                )}
              </span>
              {!create && (
                <>
                  <del data-empty={r.before === '—' ? 'true' : 'false'}>
                    <VisuallyHidden>было: </VisuallyHidden>
                    {r.before}
                  </del>
                  <span className="arrow" aria-hidden="true">
                    →
                  </span>
                </>
              )}
              <ins>
                {!create && <VisuallyHidden>стало: </VisuallyHidden>}
                {r.after}
              </ins>
            </Row>
          );
        })}
      </List>
      {rows.length > limit && (
        <More
          type="button"
          aria-expanded={expanded}
          aria-controls={listId}
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Свернуть' : `Показать ещё ${hidden}`}
        </More>
      )}
    </Wrap>
  );
}
